import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { BulkAction } from '../../models/bulk-action';
import { ModalDirective } from 'ngx-bootstrap';
import { OnyxSummaryEvent } from '../../models/event.summary';
import { Constants } from '../../../shared/constants';
import { Page } from '../../../shared/models/page';
import { List, OrderedSet } from 'immutable';
import { DateUtil } from '../../../shared/utils/date.util';
import { EventListStore } from '../../service/event-list.store';
import { LogService } from '../../../shared/log/log.service';
import { PaymentStore } from '../../service/payment.store';
import { Payment } from '../../models/payment';
import { PaymentType } from '../../models/payment-type';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';
import { concatMap } from 'rxjs/operators';
import { from } from 'rxjs/observable/from';
import { ConfirmationModalConfig } from '../../../shared/confirmation-modal/confirmation-modal-config';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { PaymentStatus } from '../../models/payment-status.enum';
import { Observable } from 'rxjs/Observable';
import { EventSearchFilter } from '../../models/event-filter';
import { EventFilterType } from '../../models/event-filter-type-enum';
import { EnvironmentConfiguration } from '../../../environment/services/environment.configuration';


@Component({
  selector: 'pioneer-bulk-action-popup',
  templateUrl: './bulk-action-popup.component.html',
  styleUrls: ['./bulk-action-popup.component.css']
})
export class BulkActionPopupComponent implements OnInit {

  @ViewChild(ConfirmationModalComponent)
  confirmationModal: ConfirmationModalComponent;

  @Output()
  closed = new EventEmitter<any>();

  @ViewChild(ModalDirective)
  bulkActionModal: ModalDirective;

  bulkAction: BulkAction = BulkAction.PAYMENT_REQUEST;

  eventsFiltered: List<OnyxSummaryEvent>;

  noEventsFound: boolean;

  eventCountForTranslate: Object;

  selectedEvents: OnyxSummaryEvent[] = [];
  allEventsChecked: boolean;

  eventsProcessed: number;

  confirmationModalConfig = new ConfirmationModalConfig();

  config = {
    backdrop: 'static',
    keyboard: false
  };

  bulkActionInfo: string;
  bulkActionInProcess = false;

  constructor(private eventListStore: EventListStore,
              private logService: LogService,
              private paymentStore: PaymentStore,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
              private environment: EnvironmentConfiguration) { }

  ngOnInit() {
  }

  public get BulkAction() {
    return BulkAction;
  }

  open(action: BulkAction): void {
    this.bulkActionInProcess = false;
    this.eventsProcessed = 0;
    this.bulkAction = action;
    const filtersSet = this.buildEventFilter();

    // Load and filter events
    this.eventListStore.loadBulkActionEvents(0, this.environment.events.maxEvents, filtersSet).take(1)
      .subscribe((eventPage: Page<OnyxSummaryEvent>) => {

        switch ( this.bulkAction ) {
          case BulkAction.PAYMENT_REQUEST:
            this.eventsFiltered = eventPage.content.filter(event =>
                !event.propertySubscribed && this.hasEventFinished(event) && !event.payStatus
                && event.dueAmount && Number(event.dueAmount) > 0)
              .toList();
            break;
          case BulkAction.PAID_DIRECT:
            this.eventsFiltered = eventPage.content.filter(event =>
              !event.propertySubscribed && this.hasEventFinished(event)
              && (!event.payStatus
                || event.payStatus === PaymentStatus.REQUESTED.toString()
                || event.payStatus === PaymentStatus.RESENT_REQUEST.toString()
                || event.payStatus === PaymentStatus.REQUEST_REJECTED.toString()))
              .toList();
            break;
          case BulkAction.CANCELLED:
            this.eventsFiltered = eventPage.content.filter(event =>
              !event.payStatus || event.payStatus !== PaymentStatus.CANCELLED.toString())
              .toList();
            break;
          }

        // Check number of events loaded
        if (!this.eventsFiltered || this.eventsFiltered.size === 0) {
          this.noEventsFound = true;
        } else {
          this.noEventsFound = false;
          this.eventCountForTranslate = {
            eventCount: this.eventsFiltered.size
          };
        }

        // Uncheck all events
        this.allEventsChecked = false;
        this.checkUncheckAll();

        this.bulkActionModal.show();
      });
  }

  onCancel() {
    this.closed.emit(true);
    this.bulkActionModal.hide();
  }

  getEventNameLine1(name: string): string {
    if (name.length > Constants.EVENTS_NAME_MAX_SIZE) {
      return name.substring(0, Constants.EVENTS_NAME_MAX_SIZE);
    } else {
      return name;
    }
  }

  getEventNameLine2(name: string): string {
    if (name.length > Constants.EVENTS_NAME_MAX_SIZE) {
      const line2: string = name.substring(Constants.EVENTS_NAME_MAX_SIZE);
      if (line2.length > Constants.EVENTS_NAME_MAX_SIZE) {
        return line2.substring(0, Constants.EVENTS_NAME_MAX_SIZE);
      } else {
        return line2;
      }
    } else {
      return null;
    }
  }

  hasEventFinished(event: OnyxSummaryEvent) {
    return DateUtil.differenceInCalendarDays(DateUtil.parseDate(event.endDate), new Date()) < 0;
  }

  hasEventStarted(event: OnyxSummaryEvent) {
    return DateUtil.differenceInCalendarDays(DateUtil.parseDate(event.startDate), new Date()) < 0;
  }

  checkUncheckAll() {
    this.eventsFiltered.forEach(event => {
      event.isSelected = this.allEventsChecked;
    });
    this.getCheckedItemList();
  }

  isAllSelected() {
    this.allEventsChecked = this.eventsFiltered.every(item => {
      return item.isSelected === true;
    });
    this.getCheckedItemList();
  }

  getCheckedItemList() {
    this.selectedEvents = [];
    this.eventsFiltered.forEach(event => {
      if (event.isSelected) {
        this.selectedEvents.push(event);
      }
    });
  }

  disableBulkActionButton() {
    return !this.selectedEvents || this.selectedEvents.length === 0;
  }

  confirmBeforeBulkActionHandler(value) {
    if (value) {
      this.onBulkAction();
    }
    this.bulkActionModal.show();
  }

  confirmBeforeBulkAction(): void {

    let label = '';
    switch ( this.bulkAction ) {
      case BulkAction.PAYMENT_REQUEST:
        label = 'EVENTS.BULK_ACTION.CONFIRM_PAYMENT_REQUEST';
        break;
      case BulkAction.PAID_DIRECT:
        label = 'EVENTS.BULK_ACTION.CONFIRM_PAID_DIRECT';
        break;
      case BulkAction.CANCELLED:
        label = 'EVENTS.BULK_ACTION.CONFIRM_CANCELLED';
        break;
      default:
        label = 'EVENTS.BULK_ACTION.CONFIRM_PAYMENT_REQUEST';
    }

    this.confirmationModalConfig.confirm = this.translateService.instant('EVENTS.BULK_ACTION.CONFIRM_PROCEED');
    this.confirmationModalConfig.title = this.translateService.instant(label, { eventCount: this.selectedEvents.length });
    this.bulkActionModal.hide();
    this.confirmationModal.open();
  }

  onBulkAction() {
    this.logService.info(JSON.stringify(this.selectedEvents));

    switch ( this.bulkAction ) {
      case BulkAction.PAYMENT_REQUEST:
        this.processPaymentRequests();
        break;

      case BulkAction.PAID_DIRECT:
        this.processPaidDirect();
        break;

      case BulkAction.CANCELLED:
        this.processCancelled();
        break;

    }
  }

  private processPaymentRequests() {
    let eventPayments: { ev: OnyxSummaryEvent, pay: Payment }[] = [];
    this.selectedEvents.forEach( event => {
      const payment = new Payment({
        commissionAmount: event.dueAmount,
        description: '',
        commissionCurrency: event.currency,
        eventId: event.eventId,
        type: PaymentType.FINAL_PAYMENT
      });
      eventPayments.push({ ev: event, pay: payment });
    });
    this.processPayments(eventPayments);
  }

  private processPaidDirect() {
    let eventPayments: { ev: OnyxSummaryEvent, pay: Payment }[] = [];
    this.selectedEvents.forEach( event => {
      const payment = new Payment({
        commissionAmount: event.paymentAmount,
        description: '',
        commissionCurrency: event.currency,
        eventId: event.eventId,
        type: PaymentType.PAID_DIRECT
      });
      eventPayments.push({ ev: event, pay: payment });
    });
    this.processPayments(eventPayments);
  }

  private processCancelled() {
    let eventPayments: { ev: OnyxSummaryEvent, pay: Payment }[] = [];
    this.selectedEvents.forEach( event => {
      const payment = new Payment({
        commissionAmount: null,
        description: '',
        commissionCurrency: event.currency,
        eventId: event.eventId,
        type: PaymentType.EVENT_CANCELLED
      });
      eventPayments.push({ ev: event, pay: payment });
    });
    this.processPayments(eventPayments);
  }

  private processPayments(eventPayments: { ev: OnyxSummaryEvent, pay: Payment }[]) {
    let counter = 0;
    this.bulkActionInfo =
      `Processing ${counter} out of ${eventPayments.length} payments...`;
    this.bulkActionInProcess = true;
    from(eventPayments).pipe(
      concatMap(obj => {
        this.bulkActionInfo =
          `Processing ${++counter} out of ${eventPayments.length} payments for Event Id ${obj['ev'].eventReferenceNumber}...`;
        this.logService.info(this.bulkActionInfo);
        return this.savePayment(obj['ev'].payStatus, obj['pay']);
      })
    ).subscribe({
      next: response => {
        this.bulkActionInfo =
          `Processed ${counter} out of ${eventPayments.length} payments`;
        this.logService.info(`${this.bulkActionInfo}, Response: ${response}`);
      },
      error: error =>  {
        this.logService.error(`Error processing ${counter} out of ${eventPayments.length} payments, Error: ${error}`);
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error processing Payment: ${error}`);
      },
      complete: () => {
        this.onCancel();
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENTS_SAVED');
      }
    });
  }

  private savePayment(eventPayStatus: string, payment: Payment): Observable<any> {
    if (eventPayStatus === PaymentStatus.REQUEST_REJECTED.toString()) {
      return this.paymentStore.updateBulkActionPayment(payment);
    } else if ((payment.type === PaymentType.EVENT_CANCELLED
              || payment.type === PaymentType.PAID_DIRECT)
              && (eventPayStatus === PaymentStatus.REQUESTED.toString()
              || eventPayStatus === PaymentStatus.RESENT_REQUEST.toString())) {
      return this.paymentStore.updateBulkActionPayment(payment);
    } else {
      return this.paymentStore.addBulkActionPayment(payment);
    }
  }

  buildEventFilter(): OrderedSet<EventSearchFilter> {
    let filtersSet: OrderedSet<EventSearchFilter>;

    switch (this.bulkAction) {
      case BulkAction.CANCELLED:
        const filter =  new EventSearchFilter({
          filterField: EventFilterType.DATE_AFTER.toString(),
          filterValue: DateUtil.formatDate(new Date())
        });
        filtersSet = OrderedSet([filter]);
        break;
      default:
        filtersSet = OrderedSet([]);
    }

    return filtersSet;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/bulk-action/bulk-action-popup/bulk-action-popup.component.ts